var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/builder/HttpClientBuilder.ts
import isEmpty from "lodash.isempty";
var _HttpClientBuilder = class _HttpClientBuilder {
  headers = {};
  timeout;
  baseURL = "";
  logger;
  client;
  shouldEmptyBaseURL = false;
  protocol;
  static from({ client }) {
    return new _HttpClientBuilder({
      client
    });
  }
  constructor({ client }) {
    this.client = client;
  }
  setBaseURL(baseURL) {
    this.baseURL = baseURL;
    return this;
  }
  setHeaders(headers) {
    this.headers = headers;
    return this;
  }
  setTimeout(timeout) {
    this.timeout = timeout;
    return this;
  }
  setLogger(logger) {
    this.logger = logger;
    return this;
  }
  withoutBaseURL() {
    this.shouldEmptyBaseURL = true;
    return this;
  }
  withProtocol(protocol) {
    this.protocol = protocol;
    return this;
  }
  invoke() {
    if (!this.shouldEmptyBaseURL && isEmpty(this.baseURL)) {
      throw new Error("HttpClientBuilder: baseURL is required.");
    }
    const client = this.client.of({
      baseURL: this.baseURL,
      headers: this.headers,
      timeout: this.timeout,
      logger: this.logger,
      protocol: this.protocol
    });
    this.clear();
    return client;
  }
  clear() {
    this.headers = {};
    this.timeout = void 0;
    this.baseURL = "";
    this.shouldEmptyBaseURL = false;
  }
};
__name(_HttpClientBuilder, "HttpClientBuilder");
var HttpClientBuilder = _HttpClientBuilder;

// src/browser/BrowserHttpClient.ts
import isEmpty2 from "lodash.isempty";
import isNil from "lodash.isnil";
import isPlainObject from "lodash.isplainobject";
import qs from "query-string";
var DEFAULT_TIMEOUT = 5 * 1e3;
var _BrowserHttpClient = class _BrowserHttpClient {
  baseURL;
  headers;
  constructor(options) {
    this.baseURL = options.baseURL;
    this.headers = options.headers ?? {};
  }
  static of(params) {
    return new _BrowserHttpClient(params);
  }
  async get(options) {
    return this.request({ method: "GET", ...options });
  }
  async post(options) {
    return this.request({ method: "POST", ...options });
  }
  async put(options) {
    return this.request({ method: "PUT", ...options });
  }
  async patch(options) {
    return this.request({ method: "PATCH", ...options });
  }
  async delete(options) {
    return this.request({ method: "DELETE", ...options });
  }
  async request({
    method,
    queryObject,
    url,
    init
  }) {
    const endpoint = this.getRequestURL({ queryObject, url });
    const result = await fetch(endpoint, this.getOptions({ init, method }));
    if (result.status === 204) {
      return void 0;
    }
    if (!result.ok) {
      throw {
        status: result.status,
        statusText: result.statusText,
        message: await this.safeParseJSON(result)
      };
    }
    if (result.headers.get("content-length") === "0") {
      return void 0;
    }
    if (result.headers.get("content-type")?.includes("application/json")) {
      return await this.safeParseJSON(result);
    }
    return await result.text();
  }
  getOptions({
    method,
    init
  }) {
    return {
      ...init,
      headers: this.mergeHeaders({
        initHeaders: init?.headers,
        baseHeaders: this.headers
      }),
      signal: AbortSignal.timeout(DEFAULT_TIMEOUT),
      method
    };
  }
  getRequestURL({
    url,
    queryObject
  }) {
    if (isNil(queryObject) || isEmpty2(queryObject)) {
      return `${this.baseURL}${url}`;
    }
    const queryString = qs.stringify(queryObject, {
      arrayFormat: "comma",
      arrayFormatSeparator: ",",
      skipNull: true,
      skipEmptyString: true
    });
    if (isEmpty2(queryString)) {
      return `${this.baseURL}${url}`;
    }
    return [`${this.baseURL}${url}`, queryString].join("?");
  }
  mergeHeaders({
    initHeaders,
    baseHeaders
  }) {
    if (isNil(initHeaders) || isEmpty2(initHeaders)) {
      return baseHeaders;
    }
    if (!isPlainObject(initHeaders)) {
      console.warn("HttpClient: initHeaders is not a plain object", {
        meta: { initHeaders }
      });
    }
    return {
      ...baseHeaders,
      ...initHeaders
    };
  }
  async safeParseJSON(result) {
    try {
      return await result.json();
    } catch {
      console.warn("BrowserHttpClient: Failed to parse JSON");
      return result;
    }
  }
};
__name(_BrowserHttpClient, "BrowserHttpClient");
var BrowserHttpClient = _BrowserHttpClient;

// src/browser/index.ts
var clientBuilder = HttpClientBuilder.from({
  client: BrowserHttpClient
});
export {
  clientBuilder
};
/*! For license information please see index.mjs.LEGAL.txt */
//# sourceMappingURL=index.mjs.map
